/**
 * `PlatformPayments` on Electron — the IPC-backed `BlockChypClient` (A1.2).
 *
 * The seam this sits in already existed. The Capacitor plugin's own type
 * documentation calls its facade "Node-compatible", and the official SDK client
 * the app already uses is the same five methods plus ninety it does not call on
 * this path. So the desktop difference is confined to **where the provider call
 * executes**, never to how a sale is orchestrated: preparation, finalization and
 * recovery keep running through the shared V4/V5 engine exactly as on mobile
 * (A1.1 — there is no desktop checkout engine, and this file is not the start of
 * one).
 *
 * ## Why the renderer must never construct the real client here (A1.3/A1.4)
 *
 * `BlockChypClient` is `ReturnType<typeof BlockChyp.newClient>` — a client
 * seeded with `apiKey`, `bearerToken` and `signingKey`. Building it in the
 * renderer would put a signing key in the JS heap of a browser context that also
 * runs the whole POS UI. On desktop the SDK therefore executes in the Electron
 * main process, and `createBlockChypClient` returns this proxy instead.
 *
 * ## Two behaviours that are not obvious from the interface
 *
 * **`PAYMENT_DUPLICATE_DISPATCH` becomes a status query, never a second charge**
 * (A1.6). Main refuses a `charge` whose `transactionRef` it has already
 * dispatched — the marker is on disk in main, written *before* the SDK call, so
 * it survives the renderer crash or reload that would otherwise lose the ref.
 * This client converts that refusal into `transactionStatus(transactionRef)` and
 * returns provider truth. That is the same resolution the existing recovery path
 * performs (`pay-first.ts:291` — "never dispatches a new one"); it is reached
 * here too so that a duplicate can never become a double charge even if a caller
 * retried without going through recovery.
 *
 * **A bridge failure is a throw, not a degraded value.** If the shell is gone
 * mid-charge, this rejects and the engine's existing UNKNOWN handling takes
 * over — `transactionStatus` by `transactionRef`. It never retries `charge`.
 *
 * ## Methods outside the five-method facade
 *
 * The official client exposes ~90 methods (batches, payment links, surcharge
 * review, terminal activation…). None of them is on the checkout path and none
 * has a bridge channel, so reaching one on desktop throws
 * `PlatformCapabilityUnsupportedError` rather than returning `undefined` and
 * failing somewhere less informative. In particular there is deliberately **no
 * keyed / card-not-present path** (Q13): `charge()` is terminal-directed and
 * T3.B was told not to invent one.
 */
import type { BlockChypClient, BlockChypCredentials } from 'src/utils/Services/blockchyp.service';

import { PlatformCapabilityUnsupportedError } from '../errors';
import { noteElectronChargeEnvelope } from './dispatch-reconcile.electron';
import { paymentsBridge, bridgeErrorCodeOf, unwrapBridgeResult } from './bridge';

/** The SDK returns axios-shaped replies; every caller reads `.data`. */
interface SdkReply<T> {
  data: T;
}

const wrap = <T>(data: T): SdkReply<T> => ({ data });

/**
 * Keys a `Proxy` must answer `undefined` for.
 *
 * `then` is the dangerous one: `createBlockChypClient` resolves *to* this
 * object, and a thenable would make `await` try to adopt it as a promise and
 * hang the sale. `Symbol` keys cover structured-clone probes, `console.log`
 * inspection and `util.inspect`.
 */
const PASSTHROUGH_KEYS = new Set(['then', 'catch', 'finally', 'toJSON', 'constructor']);

/** Defaults the bridge request schemas require but the SDK treats as optional. */
const DEFAULT_TIMEOUT_SECONDS = 30;

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new PlatformCapabilityUnsupportedError(
      `blockchyp.${field}`,
      'electron',
      `The desktop BlockChyp bridge requires a non-empty '${field}'.`
    );
  }
  return value;
}

function optionalTimeout(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.min(Math.trunc(value), 600)
    : DEFAULT_TIMEOUT_SECONDS;
}

type ChargeRequest = Record<string, unknown>;

/**
 * Builds the wire request field by field.
 *
 * Never a spread of the caller's object. The bridge schema is `.strict()` and
 * would reject a stray key anyway, but the rejection would arrive as an opaque
 * `INVALID_REQUEST` in the middle of a sale. More importantly, rebuilding is
 * what guarantees that `pan`, `cvv`, `pinBlock` or a credential cannot leave the
 * renderer on this channel even if some future caller put one on the request.
 */
function toChargeRequest(request: ChargeRequest) {
  return {
    terminalName: requireString(request.terminalName, 'terminalName'),
    amount: requireString(request.amount, 'amount'),
    transactionRef: requireString(request.transactionRef, 'transactionRef'),
    test: request.test === true,
    promptForTip: request.promptForTip === true,
    description: typeof request.description === 'string' ? request.description : '',
    timeout: optionalTimeout(request.timeout),
    ...(typeof request.force === 'boolean' ? { force: request.force } : {}),
    ...(typeof request.async === 'boolean' ? { async: request.async } : {}),
  };
}

/**
 * The five methods A1.2 names, over the six allow-listed `pc:payments:*`
 * channels. Everything else on the SDK's surface is unreachable by design.
 */
function createFacade() {
  const transactionStatus = async (request: Record<string, unknown>) => {
    const transactionRef =
      typeof request.transactionRef === 'string' ? request.transactionRef : undefined;
    const transactionId =
      typeof request.transactionId === 'string' ? request.transactionId : undefined;

    return wrap(
      unwrapBridgeResult(
        'payments',
        await paymentsBridge().transactionStatus({
          ...(transactionId === undefined ? {} : { transactionId }),
          ...(transactionRef === undefined ? {} : { transactionRef }),
          test: request.test === true,
          timeout: optionalTimeout(request.timeout),
        })
      )
    );
  };

  return {
    async charge(request: ChargeRequest) {
      const wire = toChargeRequest(request);
      const result = await paymentsBridge().charge(wire);
      // T4-07: observation only — see `dispatch-reconcile.electron.ts`.
      noteElectronChargeEnvelope(result, { terminalName: wire.terminalName, test: wire.test });

      if (result.ok) return wrap(result.value);

      if (result.error.code === 'PAYMENT_DUPLICATE_DISPATCH') {
        // A1.6. Main already refused to send a second charge; the only correct
        // next move is to read provider truth for this ref. Deliberately NOT a
        // retry, and deliberately not a failure: the money may already have
        // moved, and only the gateway knows.
        return transactionStatus({
          transactionRef: wire.transactionRef,
          test: wire.test,
          timeout: wire.timeout,
        });
      }

      // Everything else — including an UNKNOWN dispatch — throws, so the
      // engine's existing BlockChyp error handling classifies it exactly as it
      // does a native or web SDK rejection.
      throw bridgeThrow(result.error.code, result.error.message);
    },

    async clear(request: Record<string, unknown>) {
      return wrap(
        unwrapBridgeResult(
          'payments',
          await paymentsBridge().clear({
            terminalName: requireString(request.terminalName, 'terminalName'),
            test: request.test === true,
            timeout: optionalTimeout(request.timeout),
          })
        )
      );
    },

    async terminalStatus(request: Record<string, unknown>) {
      return wrap(
        unwrapBridgeResult(
          'payments',
          await paymentsBridge().terminalStatus({
            terminalName: requireString(request.terminalName, 'terminalName'),
            test: request.test === true,
            timeout: optionalTimeout(request.timeout),
          })
        )
      );
    },

    transactionStatus,

    async release(): Promise<void> {
      // Best effort by design. `release` drops main's client and forgets its
      // registered secrets; a shell that has already gone away has done both.
      // Throwing here would turn a teardown into a failed sale.
      try {
        unwrapBridgeResult('payments', await paymentsBridge().release({}));
      } catch {
        // Intentionally swallowed — see above.
      }
    },
  };
}

/** A bridge error as a throwable, carrying the shell's code for callers that branch. */
function bridgeThrow(code: string, message: string): Error {
  const error = new Error(message);
  error.name = 'BlockChypBridgeError';
  Object.defineProperty(error, 'bridgeErrorCode', { value: code, enumerable: true });
  return error;
}

/** Re-exported so a caller can branch on the shell's code without string matching. */
export { bridgeErrorCodeOf };

type Facade = ReturnType<typeof createFacade>;

/**
 * Provisions the credentials into main and returns the IPC-backed client.
 *
 * The credentials pass through this one call on their way from the backend
 * device-sync payload into main's `safeStorage` custody (A1.5). They are not
 * retained here, not closed over by the returned client, and — by the response
 * schema, which carries only `{ provisioned, persisted }` — never come back.
 *
 * Provisioning on every construction is deliberate. It is idempotent in main,
 * it costs one IPC round trip per client, and it means a shell that lost its
 * credentials (fresh install, cleared store, an OS keyring that could not
 * persist them) recovers without any separate re-provisioning flow.
 */
export async function createElectronBlockChypClient(
  credentials: BlockChypCredentials
): Promise<BlockChypClient> {
  unwrapBridgeResult(
    'payments',
    await paymentsBridge().provisionCredentials({
      apiKey: credentials.apiKey,
      bearerToken: credentials.bearerToken,
      signingKey: credentials.signingKey,
    })
  );

  const facade = createFacade();

  /**
   * Presented as the full `BlockChypClient` because every existing call site is
   * typed against it (A1.2: the call sites are rebound and "otherwise left
   * alone"). The proxy is what keeps that honest — an un-bridged method is a
   * loud, named refusal rather than a `TypeError: x.batchHistory is not a
   * function` three frames away from the cause.
   */
  return new Proxy(facade, {
    get(target, property, receiver) {
      if (typeof property === 'symbol' || PASSTHROUGH_KEYS.has(property)) {
        return Reflect.get(target, property, receiver) as unknown;
      }
      if (property in target) {
        return Reflect.get(target, property, receiver) as unknown;
      }
      return () => {
        throw new PlatformCapabilityUnsupportedError(
          `blockchyp.${property}`,
          'electron',
          `'${property}' has no allow-listed bridge channel, so it does not execute on the ` +
            'desktop register. Desktop BlockChyp is CLOUD terminal operations only: charge, ' +
            'clear, terminalStatus, transactionStatus and release.'
        );
      };
    },
    has(target, property) {
      return property in target;
    },
  }) as unknown as BlockChypClient;
}

export type { Facade as ElectronBlockChypFacade };
